import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useReducer, useRef } from 'react';
import { REPLAY_NOTIFICATIONS } from '../store/notifications';
import {
  NEW_REPLAY_MESSAGES,
  REPLAY_LAST_EVENTS,
  REPLAY_NOW,
  REPLAY_START,
} from '../store/replayEvents';
import {
  AssetState,
  AssetUpdateType,
  LiveMapState,
  LiveStateOrFullUpdate,
  LiveStateUpdate,
} from '../util/Events/Messages';
import { ExtendedMap } from '../util/mapUtils';
import { StreamData, liveStateReducer, INITIAL_STATE } from './stream';

export const useGeomobyReplayStream = (): StreamData => {
  const startDate = useMemo(() => new Date(), []);
  const [liveState, liveDispatch] = useReducer(liveStateReducer, INITIAL_STATE);
  const lastLMU = useRef<LiveStateUpdate[]>([]);
  const lastStreamed = useRef<Date | undefined>(undefined);

  const lastEvents = useAtomValue(REPLAY_LAST_EVENTS);
  const now = useAtomValue(REPLAY_NOW);
  const start = useAtomValue(REPLAY_START);
  const lastNow = useRef(now);
  const lastNegative = useRef(Symbol());
  lastNegative.current = now < lastNow.current ? Symbol() : lastNegative.current;
  const lastNegativeCurrent = lastNegative.current;
  lastNow.current = now;
  const [queue, setQueue] = useAtom(NEW_REPLAY_MESSAGES);
  const setNotifications = useSetAtom(REPLAY_NOTIFICATIONS);

  // Sets the initial state from the static map data and initial batch of events
  useEffect(() => {
    if (lastEvents && lastEvents.length > 0) {
      const msgs: LiveStateUpdate[] = lastEvents;
      const actualInitialState: LiveMapState = msgs.reduce(
        (state: LiveMapState, msg: LiveStateUpdate) => liveStateReducer(state, msg),
        INITIAL_STATE,
      );
      const updateEvent: LiveStateOrFullUpdate = {
        type: AssetUpdateType.FullStateUpdate,
        data: actualInitialState,
      };
      liveDispatch(updateEvent);
      lastLMU.current = [...lastLMU.current, ...msgs];
    }
  }, [lastEvents, lastNegativeCurrent]);

  useEffect(() => {
    setNotifications(liveState.notifications);
  }, [liveState, setNotifications]);

  // When reseting
  useEffect(() => {
    if (String(start) === String(now)) {
      const initialReplayState = {
        assets: new ExtendedMap<string, AssetState>(),
        lastUpdate: lastNow.current,
        startDate: lastNow.current,
        notifications: [],
      };
      const resetEvent: LiveStateOrFullUpdate = {
        type: AssetUpdateType.FullStateUpdate,
        data: initialReplayState,
      };
      liveDispatch(resetEvent);
      lastLMU.current = [];
      setNotifications([]);
    }
  }, [start, now, setNotifications]);

  // Dispatches replay events as they come in
  useEffect(() => {
    if (queue && queue.length) {
      let messages: LiveStateUpdate[] = [];
      if (queue.length > 0) {
        setQueue(queue => {
          messages = queue;
          return [];
        });
      }
      lastStreamed.current = new Date();
      messages.forEach(update =>
        liveDispatch({
          ...update,
          lastStreamed: lastStreamed.current
            ? { when: lastStreamed.current, event: update.type }
            : undefined,
        }),
      );
      lastLMU.current = messages;
    }
  }, [queue, setQueue]);

  const result = {
    state: {
      ...liveState,
      ...(lastStreamed.current
        ? { lastStreamed: { when: lastStreamed.current, event: liveState.lastStreamed?.event } }
        : {}),
    },
    startDate,
  };
  lastStreamed.current = undefined;
  return result;
};
